/**
 * Контракт параметров фильтра для `/rates` и `/rates-conflicts` — один модуль на обе
 * страницы (задача 10577). Имена ключей одинаковы в обе стороны, поэтому ссылку со
 * страницы конфликтов можно открыть, переслать письмом и вернуться назад по кнопке
 * браузера, ничего не потеряв.
 *
 * URL — истина, localStorage — память: `EntityFilter` подставляет сохранённое значение
 * только когда в URL ключа нет (`initial-value` перевешивает хранилище).
 */

export interface RatesFilter {
  platform: string;
  client: string;
  supplier: string;
  workType: string;
  /** Только на странице конфликтов; `/rates` ключ игнорирует. */
  severity: string;
}

export const RATES_FILTER_KEYS: ReadonlyArray<keyof RatesFilter> = [
  'platform', 'client', 'supplier', 'workType', 'severity',
];

export const EMPTY_RATES_FILTER: RatesFilter = {
  platform: '', client: '', supplier: '', workType: '', severity: '',
};

function readKey(query: Record<string, any>, key: string): string {
  const value = query?.[key];
  if (value == null || value === '') {
    return '';
  }
  return String(Array.isArray(value) ? value[0] : value);
}

export function parseRatesFilter(query: Record<string, any> | null | undefined): RatesFilter {
  const source = query || {};
  return {
    platform: readKey(source, 'platform'),
    client: readKey(source, 'client'),
    supplier: readKey(source, 'supplier'),
    workType: readKey(source, 'workType'),
    severity: readKey(source, 'severity'),
  };
}

/**
 * Кладёт фильтр в query, сохраняя остальные ключи маршрута нетронутыми: пустое значение
 * ключ удаляет, чтобы в адресе не оставалось `?supplier=` без значения.
 */
export function serializeRatesFilter(
  filter: Partial<RatesFilter>,
  base: Record<string, any> = {},
): Record<string, any> {
  const query: Record<string, any> = { ...base };
  RATES_FILTER_KEYS.forEach((key) => {
    if (!(key in filter)) {
      return;
    }
    const value = filter[key];
    if (value === undefined || value === null || value === '') {
      delete query[key];
    } else {
      query[key] = String(value);
    }
  });
  return query;
}

export function ratesFilterEquals(left: RatesFilter, right: RatesFilter): boolean {
  return RATES_FILTER_KEYS.every((key) => left[key] === right[key]);
}

/** Ссылка со страницы конфликтов в `/rates` — без `severity`, он там не значит ничего. */
export function ratesPageQuery(filter: Partial<RatesFilter>): Record<string, any> {
  const query = serializeRatesFilter({ ...EMPTY_RATES_FILTER, ...filter });
  delete query.severity;
  return query;
}
